import React, { createContext, useEffect, useState } from "react";
import type { Product } from "../../api/packages/types";
import type { CartContextProps } from "./types";

const STORE_DOMAIN = "howstore.gg";

const isLocal = window.location.origin.includes("localhost");
const BASE_API = isLocal
    ? "/api/cart"
    : "https://api.centralcart.com.br/v1/webstore/cart";

// eslint-disable-next-line react-refresh/only-export-components
export const CartContext = createContext<CartContextProps | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [items, setItems] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);

    const openCart = () => setIsCartOpen(true);
    const closeCart = () => setIsCartOpen(false);
    const toggleCart = () => setIsCartOpen((p) => !p);

    useEffect(() => {
        try {
            const stored = localStorage.getItem("howstore-cart");
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setItems(parsed);
                } else {
                    console.log("Nenhum item válido encontrado no carrinho salvo.");
                }
            }
        } catch (err) {
            localStorage.removeItem("howstore-cart");
        }
    }, []);

    useEffect(() => {
        try {
            if (items.length > 0) {
                const minimal = items.map((i) => ({
                    id: i.id,
                    name: i.name,
                    image: i.image,
                    pricing: { price: i.pricing?.price ?? 0 },
                    stock: { quantity: i.stock?.quantity ?? 1 },
                }));
                localStorage.setItem("howstore-cart", JSON.stringify(minimal));
            } else {
                localStorage.removeItem("howstore-cart");
            }
        } catch (err) {
            console.warn("Falha ao salvar carrinho:", err);
        }
    }, [items]);

    const request = async (data: any) => {
        const res = await fetch(BASE_API, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-store-domain": STORE_DOMAIN,
            },
            body: JSON.stringify(data),
        });

        const responseData = await res.json().catch(() => null);
        if (!res.ok) throw new Error(responseData?.message ?? "Erro desconhecido");

        return responseData;
    };

    const addItem = async (product: Product, quantity = 1) => {
        setLoading(true);
        try {
            const existing = items.find((i) => i.id === product.id);

            if (existing) {
                const updated = items.map((i) =>
                    i.id === product.id
                        ? {
                            ...i,
                            stock: {
                                ...i.stock,
                                quantity: (i.stock?.quantity ?? 1) + quantity,
                            },
                        }
                        : i
                );
                setItems(updated);
            } else {
                setItems((prev) => [
                    ...prev,
                    { ...product, stock: { ...product.stock, quantity } },
                ]);
            }

            const payload = {
                items: [
                    {
                        id: product.id,
                        quantity: existing
                            ? (existing.stock?.quantity ?? 1) + quantity
                            : quantity,
                        fields: {},
                    },
                ],
            };

            await request(payload);
            openCart();
        } catch (err) {
            console.error("Erro ao adicionar item:", err);
        } finally {
            setLoading(false);
        }
    };

    const removeItem = async (id: number) => {
        setItems((prev) => prev.filter((i) => i.id !== id));
        try {
            await request({ items: [{ id, quantity: 0, fields: {} }] });
        } catch (err) {
            console.error("Erro ao remover item:", err);
        }
    };

    const clearCart = async (): Promise<void> => {
        setItems([]);
    };

    const increaseItem = (id: number) => {
        setItems((prev) =>
            prev.map((i) =>
                i.id === id
                    ? { ...i, stock: { ...i.stock, quantity: (i.stock?.quantity ?? 1) + 1 } }
                    : i
            )
        );
    };

    const decreaseItem = (id: number) => {
        setItems((prev) =>
            prev.map((i) =>
                i.id === id
                    ? {
                        ...i,
                        stock: {
                            ...i.stock,
                            quantity: Math.max((i.stock?.quantity ?? 1) - 1, 1),
                        },
                    }
                    : i
            )
        );
    };

    const total = items.reduce(
        (sum, i) => sum + ((i.pricing?.price ?? 0) * (i.stock?.quantity ?? 1)),
        0
    );

    const value: CartContextProps = {
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
        items,
        addItem,
        removeItem,
        increaseItem,
        decreaseItem,
        clearCart,
        total,
        loading,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
